import React, {useState, useEffect} from 'react';
import {
    Plus,
    BookOpen,
    Target,
    BarChart3,
    ArrowLeft,
    Check,
    X,
    AlertCircle,
    CheckCircle,
    ChevronRight,
    RotateCcw
} from 'lucide-react';
import {LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar} from 'recharts';

// Toast Component (moved outside)
const ToastContainer = ({toasts, removeToast}) => (
    <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
            <div
                key={toast.id}
                className={`max-w-sm p-4 rounded-lg shadow-lg border transition-all duration-300 transform ${
                    toast.type === 'success'
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : toast.type === 'error'
                            ? 'bg-red-50 border-red-200 text-red-800'
                            : toast.type === 'warning'
                                ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                                : 'bg-blue-50 border-blue-200 text-blue-800'
                }`}
            >
                <div className="flex items-center">
                    {toast.type === 'success' && <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0"/>}
                    {toast.type === 'error' && <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0"/>}
                    {toast.type === 'warning' && <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0"/>}
                    <p className="text-sm font-medium flex-1">{toast.message}</p>
                    <button
                        onClick={() => removeToast(toast.id)}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-4 w-4"/>
                    </button>
                </div>
            </div>
        ))}
    </div>
);

// Login Component (moved outside)
const LoginScreen = ({
                         phoneNumber,
                         setPhoneNumber,
                         foundUser,
                         setFoundUser,
                         completeInfoData,
                         setCompleteInfoData,
                         loginStep,
                         setLoginStep,
                         loading,
                         onPhoneSubmit,
                         onCompleteInfo,
                         onResetForm
                     }) => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
                <div className="text-center mb-8">
                    <BookOpen className="mx-auto h-12 w-12 text-indigo-600 mb-4"/>
                    <h1 className="text-3xl font-bold text-gray-900">WordFinity</h1>
                    <p className="text-gray-600 mt-2">Learn words efficiently</p>
                </div>

                {loginStep === 'phone' && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && onPhoneSubmit(e)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="+998 XX XXX XX XX"
                                required
                            />
                        </div>

                        <button
                            onClick={onPhoneSubmit}
                            disabled={loading || !phoneNumber.trim()}
                            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Checking...' : 'Login'}
                        </button>

                        <div className="text-center text-sm text-gray-500 mt-4">
                            Only registered phone numbers can access the system
                        </div>
                    </div>
                )}

                {loginStep === 'complete-info' && (
                    <div>
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-yellow-800 text-sm">
                                Welcome back, {foundUser?.name}! Please complete your profile information.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type
                                </label>
                                <select
                                    value={completeInfoData.userType}
                                    onChange={(e) => setCompleteInfoData({
                                        ...completeInfoData,
                                        userType: e.target.value
                                    })}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    required
                                >
                                    <option value="school">School</option>
                                    <option value="univer">University</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Group Number
                                </label>
                                <input
                                    type="text"
                                    value={completeInfoData.groupNumber}
                                    onChange={(e) => setCompleteInfoData({
                                        ...completeInfoData,
                                        groupNumber: e.target.value
                                    })}
                                    onKeyPress={(e) => e.key === 'Enter' && onCompleteInfo(e)}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="CS-101"
                                    required
                                />
                            </div>

                            <button
                                onClick={onCompleteInfo}
                                disabled={loading || !completeInfoData.groupNumber.trim()}
                                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Saving...' : 'Complete Login'}
                            </button>

                            <button
                                onClick={onResetForm}
                                className="w-full text-gray-600 py-2 font-medium hover:text-gray-700 transition-colors"
                            >
                                Back to Phone Entry
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Dashboard Card Component (moved outside)
const DashboardCard = ({title, description, icon, onClick, color}) => (
    <div
        onClick={onClick}
        className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer group"
    >
        <div className={`${color} text-white p-3 rounded-lg w-fit mb-4 group-hover:scale-110 transition-transform`}>
            {icon}
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
    </div>
);

// Dashboard Component (moved outside)
const Dashboard = ({
                       currentUser,
                       words,
                       dashboardStats,
                       setCurrentView,
                       onLogout
                   }) => {
    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-4xl mx-auto p-4 flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                        <p className="text-gray-600">{currentUser?.name} - {currentUser?.group_number}</p>
                        {dashboardStats && (
                            <div className="flex gap-4 text-sm text-gray-500 mt-1">
                                <span>Total Words: {dashboardStats.user?.total_words || 0}</span>
                                <span>Learned: {dashboardStats.user?.learned_words || 0}</span>
                                <span>Exercises: {dashboardStats.user?.completed_exercises || 0}</span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onLogout}
                        className="text-red-600 hover:text-red-700 font-medium px-4 py-2 border border-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                        Logout
                    </button>
                </div>
            </div>

            <div className="max-w-4xl mx-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <DashboardCard
                        title="Add Word"
                        description="Add new words to your vocabulary"
                        icon={<Plus className="h-8 w-8"/>}
                        onClick={() => setCurrentView('addWord')}
                        color="bg-green-500"
                    />

                    <DashboardCard
                        title="My Words"
                        description={`View and manage your ${words.length} words`}
                        icon={<BookOpen className="h-8 w-8"/>}
                        onClick={() => setCurrentView('myWords')}
                        color="bg-blue-500"
                    />

                    <DashboardCard
                        title="Exercise"
                        description="Practice with word exercises"
                        icon={<Target className="h-8 w-8"/>}
                        onClick={() => setCurrentView('exercise')}
                        color="bg-purple-500"
                    />

                    <DashboardCard
                        title="Statistics"
                        description="View your learning progress"
                        icon={<BarChart3 className="h-8 w-8"/>}
                        onClick={() => setCurrentView('statistics')}
                        color="bg-orange-500"
                    />
                </div>
            </div>
        </div>
    );
};

// Header Component (moved outside)
const Header = ({title, onBack}) => (
    <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto p-4 flex items-center">
            <button
                onClick={onBack}
                className="mr-4 p-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
                <ArrowLeft className="h-5 w-5"/>
            </button>
            <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        </div>
    </div>
);

// Add Word Component (moved outside)
const AddWord = ({
                     wordData,
                     setWordData,
                     loading,
                     onSubmit,
                     onBack
                 }) => {
    return (
        <div className="min-h-screen bg-gray-50">
            <Header title="Add Word" onBack={onBack}/>

            <div className="max-w-2xl mx-auto p-6">
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Word</label>
                            <input
                                type="text"
                                value={wordData.word}
                                onChange={(e) => setWordData({...wordData, word: e.target.value})}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Enter the word"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Definition</label>
                            <textarea
                                value={wordData.definition}
                                onChange={(e) => setWordData({...wordData, definition: e.target.value})}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 h-24"
                                placeholder="Enter the definition"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Translation</label>
                            <input
                                type="text"
                                value={wordData.translation}
                                onChange={(e) => setWordData({...wordData, translation: e.target.value})}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Enter the translation"
                                required
                            />
                        </div>

                        <button
                            onClick={onSubmit}
                            disabled={loading}
                            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Adding Word...' : 'Add Word'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Statistics Component (moved outside)
const Statistics = ({statistics, currentUser, onBack}) => {
    const [activeView, setActiveView] = useState('chart');

    // Calculate total stats
    const totalStats = statistics.reduce(
        (acc, stat) => ({
            correct: acc.correct + stat.correct,
            total: acc.total + stat.total
        }),
        {correct: 0, total: 0}
    );



    const accuracy = totalStats.total > 0 ? Math.round((totalStats.correct / totalStats.total) * 100) : 0;

    // Prepare chart data
    const chartData = statistics.map(stat => ({
        date: new Date(stat.date).toLocaleDateString('en-US', {month: 'short', day: 'numeric'}),
        accuracy: stat.total > 0 ? Math.round((stat.correct / stat.total) * 100) : 0,
        correct: stat.correct,
        total: stat.total,
        incorrect: stat.total - stat.correct
    }));

    return (
        <div className="min-h-screen bg-gray-50">
            <Header title="Statistics" onBack={onBack}/>

            <div className="max-w-6xl mx-auto p-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-xl shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Questions</p>
                                <p className="text-2xl font-bold text-gray-900">{totalStats.total}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Target className="h-6 w-6 text-blue-600"/>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Correct Answers</p>
                                <p className="text-2xl font-bold text-green-600">{totalStats.correct}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-lg">
                                <CheckCircle className="h-6 w-6 text-green-600"/>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Incorrect Answers</p>
                                <p className="text-2xl font-bold text-red-600">{totalStats.total - totalStats.correct}</p>
                            </div>
                            <div className="p-3 bg-red-100 rounded-lg">
                                <X className="h-6 w-6 text-red-600"/>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Accuracy</p>
                                <p className="text-2xl font-bold text-indigo-600">{accuracy}%</p>
                            </div>
                            <div className="p-3 bg-indigo-100 rounded-lg">
                                <BarChart3 className="h-6 w-6 text-indigo-600"/>
                            </div>
                        </div>
                    </div>
                </div>

                {/* View Toggle */}
                <div className="bg-white rounded-xl shadow-sm border mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="flex">
                            <button
                                onClick={() => setActiveView('chart')}
                                className={`px-6 py-4 font-medium ${
                                    activeView === 'chart'
                                        ? 'text-indigo-600 border-b-2 border-indigo-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Accuracy Chart
                            </button>
                            <button
                                onClick={() => setActiveView('daily')}
                                className={`px-6 py-4 font-medium ${
                                    activeView === 'daily'
                                        ? 'text-indigo-600 border-b-2 border-indigo-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Daily Breakdown
                            </button>
                        </nav>
                    </div>

                    <div className="p-6">
                        {activeView === 'chart' ? (
                            <div className="h-80">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Accuracy Trend</h3>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3"/>
                                        <XAxis dataKey="date"/>
                                        <YAxis domain={[0, 100]}/>
                                        <Tooltip
                                            formatter={(value, name) => [
                                                `${value}${name === 'accuracy' ? '%' : ''}`,
                                                name === 'accuracy' ? 'Accuracy' : name
                                            ]}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="accuracy"
                                            stroke="#4f46e5"
                                            strokeWidth={3}
                                            dot={{fill: '#4f46e5', strokeWidth: 2, r: 4}}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Performance</h3>
                                <div className="space-y-4">
                                    {chartData.map((day, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex justify-between items-center">
                                                <div className="flex-1">
                                                    <h4 className="font-medium text-gray-900">{day.date}</h4>
                                                    <div
                                                        className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                                                        <span>✓ {day.correct} correct</span>
                                                        <span>✗ {day.incorrect} incorrect</span>
                                                        <span>Total: {day.total}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className={`text-lg font-semibold ${
                                                        day.accuracy >= 80 ? 'text-green-600' :
                                                            day.accuracy >= 60 ? 'text-yellow-600' : 'text-red-600'
                                                    }`}>
                                                        {day.accuracy}%
                                                    </div>
                                                    <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                                                        <div
                                                            className={`h-2 rounded-full ${
                                                                day.accuracy >= 80 ? 'bg-green-500' :
                                                                    day.accuracy >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                                            }`}
                                                            style={{width: `${day.accuracy}%`}}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Exercise Component (moved outside)
const Exercise = ({words, currentUser, onBack, onUpdateWord, onSaveExerciseResult, showToast}) => {
    const [currentExercise, setCurrentExercise] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [showResult, setShowResult] = useState(false);
    const [exerciseResults, setExerciseResults] = useState([]);
    const [exerciseComplete, setExerciseComplete] = useState(false);
    const [exerciseType, setExerciseType] = useState('definition'); // 'definition' or 'translation'

    // Initialize exercise
    const startExercise = (type) => {
        const availableWords = words.filter(word => word.status === 'learning' || word.status === 'learned');

        if (availableWords.length < 2) {
            showToast('You need at least 2 words to start an exercise', 'warning');
            return;
        }

        // Select random words for exercise (max 10)
        const shuffledWords = [...availableWords].sort(() => Math.random() - 0.5);
        const exerciseWords = shuffledWords.slice(0, Math.min(10, shuffledWords.length));

        const questions = exerciseWords.map(word => {
            // Create wrong answers from other words
            const wrongAnswers = availableWords
                .filter(w => w.id !== word.id)
                .sort(() => Math.random() - 0.5)
                .slice(0, 3)
                .map(w => type === 'definition' ? w.definition : w.translation);

            const correctAnswer = type === 'definition' ? word.definition : word.translation;
            const allAnswers = [correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5);

            return {
                word,
                question: type === 'definition'
                    ? `What is the definition of "${word.word}"?`
                    : `What is the translation of "${word.word}"?`,
                correctAnswer,
                options: allAnswers
            };
        });

        setExerciseType(type);
        setCurrentExercise(questions);
        setCurrentQuestionIndex(0);
        setSelectedAnswer('');
        setShowResult(false);
        setExerciseResults([]);
        setExerciseComplete(false);
    };

    const handleAnswerSelect = (answer) => {
        if (showResult) return;
        setSelectedAnswer(answer);
    };

    const handleSubmitAnswer = async () => {
        if (!selectedAnswer) return;

        const currentQuestion = currentExercise[currentQuestionIndex];
        const isCorrect = selectedAnswer === currentQuestion.correctAnswer;

        // Reset word status when exercised again - no attempts tracking
        const updatedWord = {
            ...currentQuestion.word,
            status: isCorrect ? 'learned' : 'learning',
            correct_count: 0, // Reset to 0 each time
            incorrect_count: 0 // Reset to 0 each time
        };

        onUpdateWord(updatedWord);

        const result = {
            question: currentQuestion,
            selectedAnswer,
            isCorrect,
            timeSpent: Date.now()
        };

        setExerciseResults([...exerciseResults, result]);
        setShowResult(true);

        // Auto-advance after 2 seconds
        setTimeout(() => {
            if (currentQuestionIndex < currentExercise.length - 1) {
                setCurrentQuestionIndex(currentQuestionIndex + 1);
                setSelectedAnswer('');
                setShowResult(false);
            } else {
                setExerciseComplete(true);
            }
        }, 2000);
    };

    const restartExercise = () => {
        startExercise(exerciseType);
    };

    const getExerciseStats = () => {
        const correct = exerciseResults.filter(r => r.isCorrect).length;
        const total = exerciseResults.length;
        const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
        return {correct, total, accuracy};
    };

    // Save exercise results when exercise is complete
    useEffect(() => {
        if (exerciseComplete && exerciseResults.length > 0) {
            const stats = getExerciseStats();
            const exerciseData = {
                user_id: currentUser.id,
                exercise_type: exerciseType,
                questions_answered: stats.total,
                correct_answers: stats.correct,
                incorrect_answers: stats.total - stats.correct,
                accuracy: stats.accuracy,
                completed_at: new Date().toISOString(),
                results: exerciseResults
            };

            onSaveExerciseResult(exerciseData);
        }
    }, [exerciseComplete, exerciseResults]);

    if (!currentExercise) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Header title="Exercise" onBack={onBack}/>

                <div className="max-w-2xl mx-auto p-6">
                    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                        <Target className="mx-auto h-16 w-16 text-indigo-600 mb-4"/>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Choose Exercise Type</h2>
                        <p className="text-gray-600 mb-8">Test your knowledge with different types of exercises</p>

                        <div className="space-y-4">
                            <button
                                onClick={() => startExercise('definition')}
                                className="w-full p-4 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center justify-between"
                            >
                                <span>Definition Exercise</span>
                                <ChevronRight className="h-5 w-5"/>
                            </button>

                            <button
                                onClick={() => startExercise('translation')}
                                className="w-full p-4 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-between"
                            >
                                <span>Translation Exercise</span>
                                <ChevronRight className="h-5 w-5"/>
                            </button>
                        </div>

                        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                                You
                                have {words.filter(w => w.status === 'learning' || w.status === 'learned').length} words
                                available for exercises
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (exerciseComplete) {
        const stats = getExerciseStats();
        return (
            <div className="min-h-screen bg-gray-50">
                <Header title="Exercise Complete" onBack={onBack}/>

                <div className="max-w-2xl mx-auto p-6">
                    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                        <CheckCircle className="mx-auto h-16 w-16 text-green-600 mb-4"/>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Exercise Complete!</h2>

                        <div className="grid grid-cols-3 gap-4 mb-8">
                            <div className="p-4 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">{stats.correct}</div>
                                <div className="text-sm text-green-700">Correct</div>
                            </div>
                            <div className="p-4 bg-red-50 rounded-lg">
                                <div className="text-2xl font-bold text-red-600">{stats.total - stats.correct}</div>
                                <div className="text-sm text-red-700">Incorrect</div>
                            </div>
                            <div className="p-4 bg-indigo-50 rounded-lg">
                                <div className="text-2xl font-bold text-indigo-600">{stats.accuracy}%</div>
                                <div className="text-sm text-indigo-700">Accuracy</div>
                            </div>
                        </div>

                        <div className="space-y-3 mb-8">
                            <button
                                onClick={restartExercise}
                                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                            >
                                <RotateCcw className="h-5 w-5"/>
                                Try Again
                            </button>

                            <button
                                onClick={() => {
                                    setCurrentExercise(null);
                                    setExerciseComplete(false);
                                }}
                                className="w-full bg-gray-600 text-white py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                            >
                                Choose Different Exercise
                            </button>
                        </div>

                        <div className="text-left">
                            <h3 className="font-semibold text-gray-900 mb-3">Review:</h3>
                            <div className="space-y-2">
                                {exerciseResults.map((result, index) => (
                                    <div key={index}
                                         className={`p-3 rounded-lg ${result.isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">{result.question.word.word}</span>
                                            {result.isCorrect ? (
                                                <CheckCircle className="h-5 w-5 text-green-600"/>
                                            ) : (
                                                <X className="h-5 w-5 text-red-600"/>
                                            )}
                                        </div>
                                        {!result.isCorrect && (
                                            <div className="text-sm text-gray-600 mt-1">
                                                Correct: {result.question.correctAnswer}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const currentQuestion = currentExercise[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / currentExercise.length) * 100;

    return (
        <div className="min-h-screen bg-gray-50">
            <Header title={`Exercise ${currentQuestionIndex + 1}/${currentExercise.length}`} onBack={onBack}/>

            <div className="max-w-2xl mx-auto p-6">
                {/* Progress Bar */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">Progress</span>
                        <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                            style={{width: `${progress}%`}}
                        ></div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                        {currentQuestion.question}
                    </h2>

                    <div className="space-y-3 mb-8">
                        {currentQuestion.options.map((option, index) => (
                            <button
                                key={index}
                                onClick={() => handleAnswerSelect(option)}
                                disabled={showResult}
                                className={`w-full p-4 text-left border-2 rounded-lg transition-all ${
                                    selectedAnswer === option
                                        ? showResult
                                            ? option === currentQuestion.correctAnswer
                                                ? 'border-green-500 bg-green-50'
                                                : 'border-red-500 bg-red-50'
                                            : 'border-indigo-500 bg-indigo-50'
                                        : showResult && option === currentQuestion.correctAnswer
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                } ${showResult ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span>{option}</span>
                                    {showResult && (
                                        <>
                                            {option === currentQuestion.correctAnswer && (
                                                <CheckCircle className="h-5 w-5 text-green-600"/>
                                            )}
                                            {selectedAnswer === option && option !== currentQuestion.correctAnswer && (
                                                <X className="h-5 w-5 text-red-600"/>
                                            )}
                                        </>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>

                    {!showResult && (
                        <button
                            onClick={handleSubmitAnswer}
                            disabled={!selectedAnswer}
                            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Submit Answer
                        </button>
                    )}

                    {showResult && (
                        <div className={`p-4 rounded-lg text-center ${
                            selectedAnswer === currentQuestion.correctAnswer
                                ? 'bg-green-50 text-green-800'
                                : 'bg-red-50 text-red-800'
                        }`}>
                            <div className="font-semibold">
                                {selectedAnswer === currentQuestion.correctAnswer ? 'Correct!' : 'Incorrect!'}
                            </div>
                            {selectedAnswer !== currentQuestion.correctAnswer && (
                                <div className="text-sm mt-1">
                                    The correct answer was: {currentQuestion.correctAnswer}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const MyWords = ({words, onBack, onUpdateWord, onDeleteWord, showToast, currentUser, onRefreshWords}) => {
    const [editingWord, setEditingWord] = useState(null);
    const [editData, setEditData] = useState({definition: '', translation: ''});
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('total');
    const [deleteModalWord, setDeleteModalWord] = useState(null);

    const startEdit = (word) => {
        setEditingWord(word.id);
        setEditData({
            definition: word.definition,
            translation: word.translation
        });
    };

    const cancelEdit = () => {
        setEditingWord(null);
        setEditData({definition: '', translation: ''});
    };

    const saveEdit = async (word) => {
        if (!editData.definition.trim() || !editData.translation.trim()) {
            showToast('Definition and translation cannot be empty', 'error');
            return;
        }

        setLoading(true);
        try {
            const updatedWord = {
                ...word,
                definition: editData.definition.trim(),
                translation: editData.translation.trim()
            };

            await onUpdateWord(updatedWord);
            setEditingWord(null);
            setEditData({definition: '', translation: ''});
            showToast('Word updated successfully!', 'success');
            // Refresh words after successful update
            if (onRefreshWords) {
                onRefreshWords();
            }
        } catch (error) {
            showToast('Failed to update word', 'error');
        } finally {
            setLoading(false);
        }
    };

    const deleteWord = async (word) => {
        setDeleteModalWord(word);
    };

    const confirmDelete = async () => {
        if (!deleteModalWord) return;

        setLoading(true);
        try {
            await onDeleteWord(deleteModalWord.id);
            showToast('Word deleted successfully!', 'success');
            // Refresh words after successful delete
            if (onRefreshWords) {
                onRefreshWords();
            }
        } catch (error) {
            showToast('Failed to delete word', 'error');
        } finally {
            setLoading(false);
            setDeleteModalWord(null);
        }
    };

    const cancelDelete = () => {
        setDeleteModalWord(null);
    };

    const getFilteredWords = () => {
        switch (activeTab) {
            case 'learned':
                return words.filter(w => w.status === 'learned' || w.status === 'mastered');
            case 'learning':
                return words.filter(w => w.status === 'learning');
            default:
                return words;
        }
    };

    const getStatusCounts = () => {
        const learned = words.filter(w => w.status === 'learned' || w.status === 'mastered').length;
        const learning = words.filter(w => w.status === 'learning').length;
        return {learned, learning, total: words.length};
    };

    const counts = getStatusCounts();

    return (
        <div className="min-h-screen bg-gray-50">
            <Header title="My Words" onBack={onBack}/>

            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-white rounded-xl shadow-sm">
                    <div className="border-b border-gray-200">
                        <nav className="flex">
                            {[
                                {key: 'learned', label: 'Learned', count: counts.learned},
                                {key: 'learning', label: 'Learning', count: counts.learning},
                                {key: 'total', label: 'Total', count: counts.total}
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`px-6 py-4 font-medium capitalize ${
                                        activeTab === tab.key
                                            ? 'text-indigo-600 border-b-2 border-indigo-600'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {tab.label} ({tab.count})
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="p-6">
                        <div className="grid gap-4">
                            {getFilteredWords().map((word) => (
                                <div key={word.id}
                                     className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                                    {editingWord === word.id ? (
                                        // EDIT MODE - Form with textarea and input fields
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center">
                                                <h3 className="text-lg font-semibold text-gray-900">{word.word}</h3>
                                                <span
                                                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                                        word.status === 'learned' || word.status === 'mastered'
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                {word.status}
            </span>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Definition
                                                </label>
                                                <textarea
                                                    value={editData.definition}
                                                    onChange={(e) => setEditData({
                                                        ...editData,
                                                        definition: e.target.value
                                                    })}
                                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 h-20"
                                                    placeholder="Enter definition"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Translation
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editData.translation}
                                                    onChange={(e) => setEditData({
                                                        ...editData,
                                                        translation: e.target.value
                                                    })}
                                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                    placeholder="Enter translation"
                                                />
                                            </div>

                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => saveEdit(word)}
                                                    disabled={loading}
                                                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50 flex items-center"
                                                >
                                                    <Check className="h-4 w-4 mr-1"/>
                                                    {loading ? 'Saving...' : 'Save'}
                                                </button>
                                                <button
                                                    onClick={cancelEdit}
                                                    disabled={loading}
                                                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 disabled:opacity-50 flex items-center"
                                                >
                                                    <X className="h-4 w-4 mr-1"/>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        // VIEW MODE - Display with edit/delete buttons
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-gray-900">{word.word}</h3>
                                                <p className="text-gray-600 mt-1">{word.definition}</p>
                                                <p className="text-indigo-600 mt-1">{word.translation}</p>
                                            </div>
                                            <div className="text-right">
            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-2 ${
                word.status === 'learned' || word.status === 'mastered'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
            }`}>
                {word.status}
            </span>
                                                <div className="flex space-x-2 mt-2">
                                                    <button
                                                        onClick={() => startEdit(word)}
                                                        className="px-3 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg font-medium text-sm flex items-center gap-1 transition-colors"
                                                        title="Edit word"
                                                    >
                                                        <svg className="h-4 w-4" fill="none" stroke="currentColor"
                                                             viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                                  strokeWidth={2}
                                                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                                        </svg>
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => deleteWord(word)}
                                                        className="px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg font-medium text-sm flex items-center gap-1 transition-colors"
                                                        title="Delete word"
                                                    >
                                                        <svg className="h-4 w-4" fill="none" stroke="currentColor"
                                                             viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                                  strokeWidth={2}
                                                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                                        </svg>
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {getFilteredWords().length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                    No words found in this category.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteModalWord && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
                        <div className="p-6">
                            <div
                                className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor"
                                     viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"/>
                                </svg>
                            </div>

                            <div className="text-center">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Delete Word
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    Are you sure you want to delete the word <span
                                    className="font-semibold text-gray-900">"{deleteModalWord.word}"</span>?
                                    This action cannot be undone.
                                </p>

                                <div className="bg-gray-50 p-3 rounded-lg mb-6">
                                    <p className="text-sm text-gray-700 mb-1">
                                        <span className="font-medium">Definition:</span> {deleteModalWord.definition}
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        <span className="font-medium">Translation:</span> {deleteModalWord.translation}
                                    </p>
                                </div>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={cancelDelete}
                                    disabled={loading}
                                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={loading}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                                >
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none"
                                                 viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10"
                                                        stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor"
                                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Main App Component
const VocabularyApp = () => {
    const BASE_URL = 'https://wordfinity.softmakon.uz/api';

    // State management
    const [currentUser, setCurrentUser] = useState(null);
    const [currentView, setCurrentView] = useState('login');
    const [words, setWords] = useState([]);
    const [loginStep, setLoginStep] = useState('phone');
    const [exerciseData, setExerciseData] = useState(null);
    const [statistics, setStatistics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [isRestoringSession, setIsRestoringSession] = useState(true);

    // Login specific state
    const [phoneNumber, setPhoneNumber] = useState('');
    const [foundUser, setFoundUser] = useState(null);
    const [completeInfoData, setCompleteInfoData] = useState({
        userType: 'school',
        groupNumber: ''
    });

    // Add word specific state
    const [wordData, setWordData] = useState({
        word: '',
        definition: '',
        translation: ''
    });

    // Check for saved session on app load
    useEffect(() => {
        const restoreSession = async () => {
            try {
                // NOTE: localStorage is not supported in Claude.ai artifacts
                // Uncomment these lines when using in your own environment:

                const savedUserId = localStorage.getItem('vocab_app_user_id');
                if (savedUserId) {
                    console.log('Found saved user ID:', savedUserId);
                    const userData = await getUserById(savedUserId);
                    if (userData) {
                        setCurrentUser(userData);
                        setCurrentView('dashboard');
                        showToast('Session restored successfully!', 'success');
                    } else {
                        localStorage.removeItem('vocab_app_user_id');
                        showToast('Session expired, please login again', 'warning');
                    }
                }

                // For Claude.ai artifact demo - simulate no saved session
                console.log('Session restoration disabled in Claude.ai artifacts');
            } catch (error) {
                console.error('Failed to restore session:', error);
                localStorage.removeItem('vocab_app_user_id'); // Uncomment in real environment
            } finally {
                setIsRestoringSession(false);
            }
        };

        restoreSession();
    }, []);

    // Toast notification system
    const showToast = (message, type = 'info', duration = 4000) => {
        const id = Math.random().toString(36).substr(2, 9);
        const toast = {id, message, type, duration};
        setToasts(prev => [...prev, toast]);

        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, duration);
    };

    const removeToast = (id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    // API utility functions
    const apiCall = async (endpoint, options = {}) => {
        const url = `${BASE_URL}${endpoint}`;
        const defaultOptions = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        try {
            console.log(`Making API call to: ${url}`, options);
            const response = await fetch(url, {...defaultOptions, ...options});

            console.log(`Response status: ${response.status}`);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API Error ${response.status}:`, errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('API Response data:', data);
            return data;
        } catch (error) {
            console.error('API call failed:', error);
            return null;
        }
    };

    // Get user by ID function
    const getUserById = async (userId) => {
        try {
            const response = await apiCall(`/users/${userId}/`);
            return response;
        } catch (error) {
            console.error('Failed to get user by ID:', error);
            return null;
        }
    };

    // Load user data when user logs in
    useEffect(() => {
        if (currentUser) {
            loadUserWords();
            loadUserStatistics();
            loadDashboardStats();
        }
    }, [currentUser]);

    const loadUserWords = async () => {
        try {
            const response = await apiCall(`/words/?user_id=${currentUser.id}`);
            if (response) {
                setWords(response.results || response);
                showToast('Words loaded successfully', 'success');
            } else {
                showToast('Failed to load words', 'error');
            }
        } catch (error) {
            console.error('Failed to load words:', error);
            showToast('Failed to load words', 'error');
        }
    };

    const loadUserStatistics = async () => {
    try {
        const response = await apiCall(`/user-statistic/weekly_report/?user_id=${currentUser.id}`);
        if (response && response.daily_breakdown) {
            const formattedStats = response.daily_breakdown.map(stat => ({
                date: stat.date,
                correct: stat.correct || parseInt(stat.correct_answers || '0'),
                total: stat.total || parseInt(stat.questions_answered || '0')
            }));
            setStatistics(formattedStats);
            console.log('Statistics loaded successfully:', formattedStats);
        } else {
            console.log('No statistics data available, using sample data');
        }
    } catch (error) {
        console.error('Failed to load statistics:', error);
        // Fallback to sample data
        setStatistics([]);
    }
};

    const loadDashboardStats = async () => {
        try {
            const [wordsStats, userStats] = await Promise.all([
                apiCall(`/words/by_status/?user_id=${currentUser.id}`),
                apiCall(`/users/${currentUser.id}/statistics/`)
            ]);

            setDashboardStats({
                words: wordsStats,
                user: userStats
            });
        } catch (error) {
            console.error('Failed to load dashboard stats:', error);
        }
    };

    // User authentication functions
    const findUserByPhone = async (phoneNumber) => {
        try {
            console.log('Searching for phone:', phoneNumber);
            const response = await apiCall(`/users/?phone_number=${encodeURIComponent(phoneNumber)}`);

            if (!response) {
                console.log('API call returned null - server may be offline');
                return null;
            }

            console.log('API response:', response);

            // Handle both array response and paginated response
            let users;
            if (Array.isArray(response)) {
                users = response;
            } else if (response.results && Array.isArray(response.results)) {
                users = response.results;
            } else {
                console.log('Unexpected response format:', response);
                return null;
            }

            console.log(`Found ${users.length} users`);
            return users.length > 0 ? users[0] : null;
        } catch (error) {
            console.error('Failed to find user:', error);
            return null;
        }
    };

    const updateUserInfo = async (userId, updateData) => {
        try {
            const response = await apiCall(`/users/${userId}/`, {
                method: 'PATCH',
                body: JSON.stringify(updateData)
            });
            return response;
        } catch (error) {
            console.error('Failed to update user:', error);
            return null;
        }
    };

    // Save exercise results function
    const saveExerciseResult = async (exerciseData) => {
        try {
            const response = await apiCall('/exercises/', {
                method: 'POST',
                body: JSON.stringify({
                    user_id: exerciseData.user_id,
                    exercise_type: exerciseData.exercise_type,
                    total_questions: exerciseData.questions_answered.toString(),
                    correct_answers: exerciseData.correct_answers.toString(),
                    incorrect_answers: exerciseData.incorrect_answers.toString(),
                    completed_at: new Date().toISOString()
                })
            });

            if (response) {
                console.log('Exercise result saved successfully:', response);
                showToast('Exercise results saved!', 'success');
                // Refresh statistics after saving
                loadUserStatistics();
                loadDashboardStats();
            } else {
                console.error('Failed to save exercise result');
                showToast('Failed to save exercise results', 'error');
            }
        } catch (error) {
            console.error('Failed to save exercise result:', error);
            showToast('Failed to save exercise results', 'error');
        }
    };

    // Login handlers
    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Validate phone number
        if (!phoneNumber.trim()) {
            showToast('Please enter a phone number', 'error');
            setLoading(false);
            return;
        }

        // Clean phone number (remove spaces, dashes, etc.)
        const cleanPhone = phoneNumber.trim().replace(/[\s\-\(\)]/g, '');
        console.log('Submitting phone:', cleanPhone);

        try {
            const user = await findUserByPhone(cleanPhone);
            console.log('User found:', user);

            if (user) {
                setFoundUser(user);
                showToast(`Welcome back, ${user.name}!`, 'success');

                // Check if user has complete profile info
                if (user.user_type && user.group_number && user.user_type.trim() !== '' && user.group_number.trim() !== '') {
                    console.log('User has complete info, logging in');
                    setCurrentUser(user);
                    setCurrentView('dashboard');

                    // Save user ID to localStorage for session persistence
                    // NOTE: Uncomment this line when using in your own environment:
                    localStorage.setItem('vocab_app_user_id', user.id.toString());

                    showToast('Login successful!', 'success');
                } else {
                    console.log('User needs to complete info, changing to complete-info step');
                    setLoginStep("complete-info");
                    showToast('Please complete your profile information', 'warning');
                }
            } else {
                console.log('Phone number not found in database');
                showToast('Phone number not registered. Please contact administrator.', 'error');
            }
        } catch (error) {
            console.error('Phone submit error:', error);
            showToast('Connection failed. Please check if the server is running.', 'error');
        }

        setLoading(false);
    };

    const handleCompleteInfo = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!completeInfoData.groupNumber.trim()) {
            showToast('Please enter a group number', 'error');
            setLoading(false);
            return;
        }

        try {
            const updatedUser = await updateUserInfo(foundUser.id, {
                user_type: completeInfoData.userType,
                group_number: completeInfoData.groupNumber.trim()
            });

            if (updatedUser) {
                setCurrentUser(updatedUser);
                setCurrentView('dashboard');

                // Save user ID to localStorage for session persistence
                // NOTE: Uncomment this line when using in your own environment:
                localStorage.setItem('vocab_app_user_id', updatedUser.id.toString());

                showToast('Profile completed successfully!', 'success');
            } else {
                showToast('Failed to update profile information', 'error');
            }
        } catch (error) {
            showToast('Failed to update profile information', 'error');
        }

        setLoading(false);
    };

    const resetForm = () => {
        setPhoneNumber('');
        setLoginStep('phone');
        setFoundUser(null);
        setCompleteInfoData({userType: 'school', groupNumber: ''});
    };

    // Update word function - Reset status each time a word is exercised
    const updateWord = async (updatedWord) => {
        try {
            const response = await apiCall(`/words/${updatedWord.id}/`, {
                method: 'PATCH',
                body: JSON.stringify({
                    word: updatedWord.word,
                    definition: updatedWord.definition,
                    translation: updatedWord.translation,
                    status: updatedWord.status,
                    correct_count: updatedWord.correct_count || 0,
                    incorrect_count: updatedWord.incorrect_count || 0
                })
            });
            // ... rest
        } catch (error) {
            console.error('Failed to update word:', error);
        }
    };

    const deleteWord = async (wordId) => {
        try {
            const response = await apiCall(`/words/${wordId}/`, {
                method: 'DELETE'
            });

            setWords(words.filter(word => word.id !== wordId));

            if (currentUser) {
                loadDashboardStats();
            }
        } catch (error) {
            console.error('Failed to delete word:', error);
            throw error;
        }
    };

    const handleAddWordSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!wordData.word.trim() || !wordData.definition.trim() || !wordData.translation.trim()) {
            showToast('Please fill in all fields', 'error');
            setLoading(false);
            return;
        }

        try {
            const newWordData = {
                user_id: currentUser.id,
                word: wordData.word.trim(),
                definition: wordData.definition.trim(),
                translation: wordData.translation.trim(),
                status: 'learning'
            };

            const response = await apiCall('/words/', {
                method: 'POST',
                body: JSON.stringify(newWordData)
            });

            if (response) {
                setWords([...words, response]);
                setWordData({word: '', definition: '', translation: ''});
                showToast('Word added successfully!', 'success');
            } else {
                showToast('Failed to add word. Please try again.', 'error');
            }
        } catch (error) {
            showToast('Failed to add word. Please try again.', 'error');
        }

        setLoading(false);
    };

    // Logout handler
    const handleLogout = () => {
        // Clear the history state manipulation
        window.history.replaceState(null, '', window.location.href);

        // Clear saved session
        // NOTE: Uncomment this line when using in your own environment:
        localStorage.removeItem('vocab_app_user_id');

        setCurrentUser(null);
        setCurrentView('login');
        setWords([]);
        setStatistics([]);
        setDashboardStats(null);
        setPhoneNumber('');
        setFoundUser(null);
        setLoginStep('phone');
        setCompleteInfoData({userType: 'school', groupNumber: ''});
        setWordData({word: '', definition: '', translation: ''});
        showToast('Logged out successfully', 'success');
    };

    // Show loading screen while checking for saved session
    if (isRestoringSession) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                    <BookOpen className="mx-auto h-12 w-12 text-indigo-600 mb-4 animate-pulse"/>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading...</h2>
                    <p className="text-gray-600">Checking for saved session</p>
                </div>
            </div>
        );
    }

    // Main render logic
    return (
        <div>
            <ToastContainer toasts={toasts} removeToast={removeToast}/>
            {!currentUser ? (
                <LoginScreen
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    foundUser={foundUser}
                    setFoundUser={setFoundUser}
                    completeInfoData={completeInfoData}
                    setCompleteInfoData={setCompleteInfoData}
                    loginStep={loginStep}
                    setLoginStep={setLoginStep}
                    loading={loading}
                    onPhoneSubmit={handlePhoneSubmit}
                    onCompleteInfo={handleCompleteInfo}
                    onResetForm={resetForm}
                />
            ) : currentView === 'dashboard' ? (
                <Dashboard
                    currentUser={currentUser}
                    words={words}
                    dashboardStats={dashboardStats}
                    setCurrentView={setCurrentView}
                    onLogout={handleLogout}
                />
            ) : currentView === 'addWord' ? (
                <AddWord
                    wordData={wordData}
                    setWordData={setWordData}
                    loading={loading}
                    onSubmit={handleAddWordSubmit}
                    onBack={() => setCurrentView('dashboard')}
                />
            ) : currentView === 'myWords' ? (
                <MyWords
                    words={words}
                    onBack={() => setCurrentView('dashboard')}
                    onUpdateWord={updateWord}
                    onDeleteWord={deleteWord}
                    showToast={showToast}
                    currentUser={currentUser}
                    onRefreshWords={loadUserWords}
                />
            ) : currentView === 'exercise' ? (
                <Exercise
                    words={words}
                    currentUser={currentUser}
                    onBack={() => setCurrentView('dashboard')}
                    onUpdateWord={updateWord}
                    onSaveExerciseResult={saveExerciseResult}
                    showToast={showToast}
                />
            ) : currentView === 'statistics' ? (
                <Statistics
                    statistics={statistics}
                    currentUser={currentUser}
                    onBack={() => setCurrentView('dashboard')}
                />
            ) : (
                <Dashboard
                    currentUser={currentUser}
                    words={words}
                    dashboardStats={dashboardStats}
                    setCurrentView={setCurrentView}
                    onLogout={handleLogout}
                />
            )}
        </div>
    );
};

export default VocabularyApp;